// =============================================================================
// ui/tabs/EdgeHealth.tsx — the Edge Health dashboard.
// -----------------------------------------------------------------------------
// "Is the edge real, durable, and which way does it cut?" Read-only: tiles, two
// bar comparisons, a trend, and a diagnosis with an instruction attached.
//
// ── ⚠ BUILT WITHOUT A SCREENSHOT, DELIBERATELY, AND THE REASON IS NARROW ──
//
// The standing rule is to get a screenshot before building a tab, because
// source-reading has invented three controls that beta does not have. **This
// panel has no controls at all** — nothing to toggle, nothing to pick, one
// click-through on each extreme row. The failure that rule guards against
// cannot happen here; what CAN differ is section order and wording, and those
// come straight from the old client's own source.
//
// 🔴 THE SIDE-BY-SIDE IS STILL THE GATE. It has not been run. Until Josh sends
// the screenshot this is "built from the source and verified on his numbers",
// which is not the same claim as "matches beta".
//
// ── 🚨 THE DIAGNOSIS IS THE PRODUCT, AND IT IS NOT DECORATION ──
//
// Two traders with the same positive expectancy get OPPOSITE instructions here
// — see `domain/edgeHealth`. The sentence carries the numbers, so it is composed
// here; the instruction carries none, so it comes from the engine verbatim and
// cannot drift from the branch that chose it.
// =============================================================================

import {
  EDGE_R_MIN_SAMPLE, trendWindow,
  type EdgeHealth as EdgeHealthModel, type RBucket, type WeekPoint,
} from '../../selectors/customAnalytics';
import { num, pct, unitCompact, unitPlain } from '../format';
import type { PnlUnit } from '../../selectors/pnlUnit';

/** `Infinity` is a real answer for both of these — render it, do not hide it. */
const ratio = (v: number): string => (v === Infinity ? '∞' : num(v, 2));

const toneVar: Record<RBucket['tone'], string> = {
  bad: 'var(--red)', warn: 'var(--gold)', good: 'var(--green)',
};

export function EdgeHealthPanel({ model }: { model: EdgeHealthModel }) {
  const { kpis: k, verdict, shape } = model;
  const weeks = trendWindow(model.weeks);

  return (
    <div className="card eh-card">
      <h3 className="eh-title">
        Edge Health — is the edge real, durable, and which way does it cut?
      </h3>

      <div className="eh-tiles">
        <div className="eh-tile eh-tile-lead">
          <div className="eh-label">Expectancy / trade</div>
          <div
            className="eh-lead"
            style={{ color: k.expectancy >= 0 ? 'var(--green)' : 'var(--red)' }}
          >
            {unitCompact(k.expectancy, k.unit)}
          </div>
          <div className="muted eh-lead-sub">
            Average net over <b>{k.count}</b> trade{k.count === 1 ? '' : 's'} · net{' '}
            <b>{unitCompact(k.totalPnl, k.unit)}</b> · {k.wins}W / {k.losses}L
            {shape.avgR != null && <> · {shape.avgR >= 0 ? '+' : ''}{num(shape.avgR, 2)}R avg</>}.
          </div>
        </div>
        <Tile label="Win rate" value={pct(k.winRate, 0)} />
        <Tile
          label="Profit factor"
          value={ratio(k.profitFactor)}
          color={k.profitFactor >= 1.5 ? 'var(--green)' : k.profitFactor < 1 ? 'var(--red)' : undefined}
        />
        <Tile
          label="Payoff (win:loss)"
          value={`${ratio(verdict.payoff)}×`}
          color={verdict.payoff >= 1 ? 'var(--green)' : 'var(--red)'}
        />
      </div>

      <Section title="Average winner vs average loser">
        {/* ⚠ Both bars scale to the LARGER of the two, so the shorter one is
            visibly shorter. Scaling each to its own width would draw two equal
            bars and destroy the only comparison the section makes. */}
        {/* 🔴 THIS ROW PRINTED A BARE NUMBER. It read "125" directly above the
            loss row's "-$105" — same card, same money, one with a currency
            symbol and one without — and off dollars it also dropped the
            decimals, so an average winner of 1.19R printed as "1".

            ⚠ IT WAS A REGRESSION, not an omission from the port: before the
            unit switch was wired into this panel the fallback below was the
            dollar formatter, so this row printed "+$125" by accident. Wiring
            the switch fixed the row beneath it and left this one on a fallback
            that had quietly stopped being right. */}
        <CompareBar
          label="Avg win" value={k.avgWin} max={Math.max(k.avgWin, Math.abs(k.avgLoss)) || 1}
          color="var(--green)" display={unitCompact(k.avgWin, k.unit)}
        />
        <CompareBar
          label="Avg loss" value={Math.abs(k.avgLoss)} max={Math.max(k.avgWin, Math.abs(k.avgLoss)) || 1}
          color="var(--red)" display={unitCompact(k.avgLoss, k.unit)}
        />
      </Section>

      <Section title="Outcome shape — R-multiple distribution">
        {shape.enough ? (
          <>
            {shape.buckets.map((b) => (
              <div className="eh-bar-row" key={b.label}>
                <div className="eh-bar-label mono">{b.label}</div>
                <div className="eh-bar-track">
                  <div
                    className="eh-bar-fill"
                    style={{
                      width: `${((b.count / Math.max(1, ...shape.buckets.map((x) => x.count))) * 100).toFixed(0)}%`,
                      background: toneVar[b.tone],
                    }}
                  />
                </div>
                <div className="eh-bar-val mono">{b.count}</div>
              </div>
            ))}
            <p className="muted eh-note">
              Average{' '}
              <b style={{ color: (shape.avgR ?? 0) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                {(shape.avgR ?? 0) >= 0 ? '+' : ''}{num(shape.avgR, 2)}R
              </b>{' '}
              over the <b>{shape.sample}</b> trade{shape.sample === 1 ? '' : 's'} with a
              logged risk plan{shape.sample < k.count && <> (of {k.count} total)</>}. A fat
              right tail (1–2R / ≥2R) means your winners pay for the losers; clustering in
              0–1R is death by a thousand cuts.
            </p>
          </>
        ) : (
          /* ⚠ SAYS HOW TO UNLOCK IT rather than drawing five empty bars, which
             would read as "you never reach 2R" instead of "we cannot tell". */
          <p className="muted eh-note">
            Log a stop / risk plan on your trades to unlock the R-multiple
            distribution — only <b>{shape.sample}</b> of {k.count} here have one (need{' '}
            {EDGE_R_MIN_SAMPLE}+).
          </p>
        )}
      </Section>

      {/* 🚨 HIDDEN ENTIRELY on a one-sided book — see the engine. A "Short: $0
          over 0 trades" tile reads as a losing side, not an untraded one. */}
      {model.byDirection && (
        <Section title="Edge by direction — do you cut both ways?">
          <div className="eh-tiles">
            <DirTile label="Long" k={model.byDirection.long} />
            <DirTile label="Short" k={model.byDirection.short} />
          </div>
        </Section>
      )}

      <Section title="Durability — is the edge holding up?">
        {model.trendWorthShowing && (
          <>
            <SparkBars weeks={weeks} />
            <p className="muted eh-note">
              Expectancy per week
              {model.weeks.length > weeks.length && <> (last {weeks.length})</>} — green =
              profitable week, red = losing. A fading right side is edge decay.
            </p>
          </>
        )}
        <div className="eh-tiles eh-tiles-tight">
          <Tile label="Best win streak" value={String(k.bestStreak)} color="var(--green)" />
          <Tile label="Worst loss streak" value={String(k.worstStreak)} color="var(--red)" />
          <Tile
            label="Max drawdown"
            value={unitCompact(-Math.abs(k.maxDrawdown), k.unit)}
            color={k.maxDrawdown > 0 ? 'var(--red)' : undefined}
          />
        </div>
      </Section>

      {/* 🚨 A WHOLE SECTION THAT WAS MISSING, and only the side-by-side found it.
          Beta prints the best and worst trade of the period with their contract
          and date, and each row opens the trade. Built from the source alone,
          this simply was not there — every number that WAS on screen looked
          right, so nothing pointed at the gap. */}
      {/* 🚨 A WHOLE SECTION THAT WAS MISSING, and only the side-by-side found it.
          Beta prints the best and worst trade of the period with their contract
          and date, and each row opens the trade. Built from the source alone,
          this simply was not there — every number that WAS on screen looked
          right, so nothing pointed at the gap. */}
      {(k.bestTrade || k.worstTrade) && (
        <Section title="Extremes">
          <ExtremeRow label="Best trade" t={k.bestTrade} value={k.bestValue} unit={k.unit} color="var(--green)" />
          <ExtremeRow label="Worst trade" t={k.worstTrade} value={k.worstValue} unit={k.unit} color="var(--red)" />
        </Section>
      )}

      <div className="eh-verdict">
        <p className="muted eh-note">
          <VerdictText model={model} />
        </p>
        {/* ⚠ VERBATIM FROM THE ENGINE. It carries no numbers, so it cannot drift
            from the branch that selected it. */}
        <p className="eh-rx"><b>Do this:</b> {verdict.prescription}</p>
      </div>
    </div>
  );
}

function VerdictText({ model }: { model: EdgeHealthModel }) {
  const { kpis: k, verdict } = model;
  const win = <b>{pct(k.winRate, 0)}</b>;
  const payoff = <b>{ratio(verdict.payoff)}×</b>;
  const avgWin = <b style={{ color: 'var(--green)' }}>{unitCompact(k.avgWin, k.unit)}</b>;
  // 🚨 UNSIGNED, AND THIS IS A DELIBERATE DIVERGENCE FROM BETA.
  //
  // Beta formats both of these with its signed money helper after taking the
  // absolute value, so its own sentences read "you're giving back **+$6** per
  // trade" and "losers (**+$105**)" — a plus sign on a loss, in the one sentence
  // on the panel whose whole job is to say the edge is not working. It is live
  // on Josh's book today, because his expectancy is negative and that branch is
  // the one that fires.
  //
  // The magnitude is already carried by the words ("giving back", "losers"), so
  // the sign is not merely redundant — it contradicts them.
  const avgLoss = <b style={{ color: 'var(--red)' }}>{unitPlain(Math.abs(k.avgLoss), k.unit)}</b>;

  if (verdict.kind === 'negative') {
    return (
      <>
        <b style={{ color: 'var(--red)' }}>Negative expectancy this period.</b> You’re
        giving back <b>{unitPlain(Math.abs(k.expectancy), k.unit)}</b> per trade on average — the
        edge isn’t showing up.
      </>
    );
  }
  if (verdict.kind === 'winRate') {
    return (
      <>
        Edge is <b style={{ color: 'var(--gold)' }}>win-rate-driven</b> — you win {win} of
        the time, but winners ({avgWin}) are barely bigger than losers ({avgLoss}), payoff{' '}
        {payoff}. Fragile: a losing run or one indiscipline day erases a lot.
      </>
    );
  }
  if (verdict.kind === 'payoff') {
    return (
      <>
        Edge is <b style={{ color: 'var(--gold)' }}>payoff-driven</b> — you only win {win},
        but winners ({avgWin}) run {payoff} your losers. The math leans on the big winners
        landing.
      </>
    );
  }
  if (verdict.kind === 'healthy') {
    return (
      <>
        <b style={{ color: 'var(--green)' }}>Healthy.</b> Positive expectancy (
        <b>{unitCompact(k.expectancy, k.unit)}</b>/trade) with a {win} win rate <i>and</i> {payoff}{' '}
        payoff — both legs of the edge are pulling. Profit factor{' '}
        <b>{ratio(k.profitFactor)}</b>.
      </>
    );
  }
  return (
    <>
      <b style={{ color: 'var(--gold)' }}>Thin but positive.</b> Expectancy is{' '}
      <b>{unitCompact(k.expectancy, k.unit)}</b>/trade, but neither your {win} win rate nor your{' '}
      {payoff} payoff is dominant — the edge is narrow.
    </>
  );
}

/**
 * ⚠ NOT CLICKABLE YET, and the label does not pretend it is. Beta opens the
 * trade from here; this client has no trade modal reachable from an analytics
 * surface, so a row that looked clickable and did nothing would be worse than
 * one that plainly is not.
 */
function ExtremeRow(
  { label, t, value, unit, color }:
  {
    label: string; t: EdgeHealthModel['kpis']['bestTrade'];
    /** 🚨 The trade’s result in the DISPLAY UNIT. Its own  is always
        dollars, so reading that names the right trade and states the wrong
        number — the defect this pair of fields exists to close. */
    value: number | null; unit: PnlUnit; color: string;
  },
) {
  if (!t) return null;
  return (
    <div className="eh-extreme">
      <div className="eh-extreme-l">{label}</div>
      <div className="eh-extreme-c">
        {t.contract ?? '—'} <span className="muted eh-extreme-d">{t.date}</span>
      </div>
      <div className="eh-extreme-v mono" style={{ color }}>{unitCompact(value, unit)}</div>
    </div>
  );
}

function Tile(
  { label, value, color }:
  { label: string; value: string; color?: string | undefined },
) {
  return (
    <div className="eh-tile">
      <div className="eh-label">{label}</div>
      <div className="eh-val" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}

function DirTile({ label, k }: { label: string; k: EdgeHealthModel['kpis'] }) {
  return (
    <div className="eh-tile">
      <div className="eh-label">{label}</div>
      <div
        className="eh-val eh-val-sm"
        style={{ color: k.expectancy >= 0 ? 'var(--green)' : 'var(--red)' }}
      >
        {unitCompact(k.expectancy, k.unit)}<span className="muted eh-per"> /trade</span>
      </div>
      <div className="muted eh-dir-sub">
        {pct(k.winRate, 0)} win · {k.count} trade{k.count === 1 ? '' : 's'} · net{' '}
        {unitCompact(k.totalPnl, k.unit)}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="eh-section">
      <div className="muted eh-label">{title}</div>
      {children}
    </div>
  );
}

/**
 * ⚠ `display` IS REQUIRED, and that is the structural half of the fix.
 *
 * It was optional with a plain-number fallback, and one of the two callers
 * simply did not pass it — silently, because an omitted optional prop is not an
 * error anywhere. A comment on the fallback asserted that "every money caller
 * now passes `display`"; one of the two did not, and the comment had been false
 * since the day it was written.
 *
 * ▶ A required prop is the version of that sentence the compiler enforces. It
 * is the same lesson as the display-unit pass: a written guarantee about how a
 * value is printed does not hold, and a signature does.
 */
function CompareBar(
  { label, value, max, color, display }:
  { label: string; value: number; max: number; color: string; display: string },
) {
  return (
    <div className="eh-bar-row">
      <div className="eh-bar-label">{label}</div>
      <div className="eh-bar-track">
        <div
          className="eh-bar-fill"
          style={{ width: `${((value / max) * 100).toFixed(0)}%`, background: color }}
        />
      </div>
      {/* ⚠ The value arrives ALREADY FORMATTED in the display unit. There is no
          fallback left to be wrong: the prop is required, so a caller that
          forgets it does not compile. */}
      <div className="eh-bar-val mono" style={{ color }}>{display}</div>
    </div>
  );
}

/**
 * A zero-baseline bar per week — above the line profitable, below it losing.
 *
 * ⚠ Scaled by the largest ABSOLUTE week, so one catastrophic week does not flatten
 * every other bar to invisibility on the profitable side.
 */
function SparkBars({ weeks }: { weeks: readonly WeekPoint[] }) {
  if (!weeks.length) return null;
  const max = Math.max(1, ...weeks.map((w) => Math.abs(w.expectancy)));
  const W = 480; const H = 46; const mid = H / 2;
  const bw = W / weeks.length;
  return (
    <svg className="eh-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" role="img"
      aria-label={`Expectancy per week across ${weeks.length} weeks`}>
      <line x1={0} y1={mid} x2={W} y2={mid} stroke="var(--border-2)" strokeWidth={1}
        vectorEffect="non-scaling-stroke" />
      {/* ⚠ GREEN, NOT BLUE — beta's `miniSparkBars` fills
          `v >= 0 ? var(--green) : var(--red)` (index.html:42889). Green and red
          are the one pair this whole app uses to mean made money / lost money,
          including in the tiles directly above this chart; a blue bar beside a
          red one reads as a second axis rather than as the good half of the
          same one. The caption below was re-worded to match the blue rather
          than the blue corrected to match the app, which is the wrong direction
          to resolve that kind of disagreement. */}
      {weeks.map((w, i) => {
        const h = (Math.abs(w.expectancy) / max) * (mid - 2);
        return (
          <rect
            key={w.weekStart}
            x={i * bw + bw * 0.18} width={bw * 0.64}
            y={w.expectancy >= 0 ? mid - h : mid}
            height={Math.max(1, h)}
            fill={w.expectancy >= 0 ? 'var(--green)' : 'var(--red)'}
            opacity={0.75}
          />
        );
      })}
    </svg>
  );
}
